import React from "react"
import { routePaths } from "../../../Routes"
import { AppPermissionScope } from "../../../Models/Client/AppPermissionScope"
import { AuthCheck } from "./AuthCheck"
import { hasResult } from "../../../Models/Client/FetchState"
import { useAssumedRoles } from "../../../Hooks/useAssumedRoles"
import { hasAppPermission } from "../../../Models/Client/ProjectRole"
import { Loader } from "dash-styled"
import { useAppState } from "../../../Hooks/useReduxState"
import {
    isAuthenticated,
    selectUserName
} from "../../../Helpers/Functions/Selectors/auth"
import { Redirect } from "react-router"
import { useMyOrgRole } from "../../../Hooks/useMyOrgRole"
import { useAppInstance } from "../../../Hooks/useAppInstance"
import { ErrorFallback } from "../ErrorBoundary/ErrorFallback"
import { Link } from "resha"
import { DashboardBase } from "../../Views/Dashboard/Sections/DashboardBase/DashboardBase"

interface AppAuthCheckProps {
    orgUsername: string
    appID: string
    scope: AppPermissionScope
    children: JSX.Element
}

export function AppAuthCheck({
    orgUsername,
    children,
    appID,
    scope
}: AppAuthCheckProps) {
    const rolesFetch = useAssumedRoles(appID)
    const orgRoleFetch = useMyOrgRole(orgUsername)
    const authenticated = useAppState(isAuthenticated)
    const instanceFetch = useAppInstance(appID)
    const myUsername = useAppState(selectUserName)
    const pathAndSearch = useAppState(
        state =>
            `${state.router.location.pathname}${state.router.location.search}`
    )

    if (!authenticated) {
        // let parent component handle redirection
        return <AuthCheck>{children}</AuthCheck>
    }

    if (
        myUsername === undefined ||
        !hasResult(rolesFetch) ||
        !hasResult(orgRoleFetch) ||
        !hasResult(instanceFetch)
    ) {
        return <Loader absolute={true} />
    }

    if (instanceFetch.isError) {
        return (
            <DashboardBase
                orgUsername={orgUsername}
                fixedContentWidth={false}
                title=""
                breadcrumbs={[]}
            >
                <ErrorFallback
                    title="Could not open project"
                    errorDetail={instanceFetch.message}
                >
                    <Link to={routePaths.appList(orgUsername)}>
                        Back to projects
                    </Link>
                </ErrorFallback>
            </DashboardBase>
        )
    }

    if (rolesFetch.isError || !hasAppPermission(rolesFetch.value, scope)) {
        return <Redirect to={routePaths.appDetail(orgUsername, appID)} />
    }

    const instance = instanceFetch.value

    if (
        instance.ownerType === "ORGANIZATION" &&
        orgUsername !== instance.ownerUsername
    ) {
        return (
            <Redirect
                to={pathAndSearch.replace(
                    `/u/${orgUsername}/`,
                    `/u/${instance.ownerUsername}/`
                )}
            />
        )
    }

    if (instance.ownerType === "USER" && orgUsername !== myUsername) {
        return (
            <Redirect
                to={pathAndSearch.replace(
                    `/u/${orgUsername}/`,
                    `/u/${myUsername}/`
                )}
            />
        )
    }

    if (orgRoleFetch.isError) {
        return <Redirect to={routePaths.dashboard()} />
    }

    return <AuthCheck>{children}</AuthCheck>
}
